import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import type { FeedbackType, FeedbackStatus } from "@snaptics/api/src/types";
import { apiClient } from "~/lib/api";
import { FeedbackList } from "~/components/FeedbackList";
import { FilterBar } from "~/components/FilterBar";

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export default function Index() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [data, setData] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const projectId = searchParams.get("project");
  const status = searchParams.get("status") as FeedbackStatus | null;
  const type = searchParams.get("type") as FeedbackType | null;
  const offset = parseInt(searchParams.get("offset") || "0", 10);
  const limit = 12;

  useEffect(() => {
    // If no project parameter, redirect to projects list
    if (!projectId) {
      navigate("/projects");
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch feedbacks
        const feedbackData = await apiClient.getFeedbacks({
          status: status || undefined,
          type: type || undefined,
          project_id: projectId || undefined,
          limit,
          offset,
        });

        setData(feedbackData);

        // Fetch project details
        try {
          const projectResponse = await fetch(`${API_URL}/api/v1/projects/${projectId}`, {
            credentials: 'include',
          });
          if (projectResponse.ok) {
            const projectData = await projectResponse.json();
            setProject(projectData.data);
          }
        } catch (err) {
          // Ignore project fetch errors
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load feedback');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, status, type, offset, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const filters = { status, type, offset, limit, projectId };

  // Helper function to build search params for filters
  const buildFilterUrl = (updates: { type?: FeedbackType | null; status?: FeedbackStatus | null; clearAll?: boolean }) => {
    const params = new URLSearchParams();

    if (updates.clearAll) {
      // Preserve project parameter when clearing filters
      if (filters.projectId) params.set("project", filters.projectId);
      return params.toString() ? `?${params.toString()}` : "?";
    }

    const newType = updates.type !== undefined ? updates.type : filters.type;
    const newStatus = updates.status !== undefined ? updates.status : filters.status;

    if (newType) params.set("type", newType);
    if (newStatus) params.set("status", newStatus);
    if (filters.projectId) params.set("project", filters.projectId);

    return params.toString() ? `?${params.toString()}` : "?";
  };

  return (
    <div className="space-y-6">
      <div>
        {project ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <a href="/projects" className="text-sm text-muted-foreground hover:text-foreground">
                ← Back to Projects
              </a>
            </div>
            <h2 className="text-3xl font-bold tracking-tight">{project.name}</h2>
            <p className="text-muted-foreground">
              {project.description || "View and manage feedback for this project"}
            </p>
          </div>
        ) : (
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Feedback</h2>
            <p className="text-muted-foreground">
              View and manage feedback from your users
            </p>
          </div>
        )}
      </div>

      {/* Improved Filter Bar */}
      <FilterBar filters={filters} onFilterChange={buildFilterUrl} />

      {/* Feedback List */}
      <FeedbackList
        data={data}
        loadMoreUrl={
          data.pagination.hasMore
            ? (() => {
                const params = new URLSearchParams();
                if (filters.type) params.set("type", filters.type);
                if (filters.status) params.set("status", filters.status);
                if (filters.projectId) params.set("project", filters.projectId);
                params.set("offset", (filters.offset + filters.limit).toString());
                return `?${params.toString()}`;
              })()
            : undefined
        }
      />
    </div>
  );
}
